/**
 * Helper function to format text with SmartTerm tooltips for technical terms
 * 
 * Usage:
 * formatWithTooltips("Your funds convert and land in your bank in minutes, not days.", ["settlement"])
 * 
 * Returns JSX with SmartTerm components wrapping technical terms
 */

import React from "react"
import { SmartTerm } from "@/components/ui/SmartTerm"

// Map of terms to their glossary keys
const TERM_MAP: Record<string, string> = {
  "settlement": "settlement",
  "settle": "settlement",
  "settling": "settlement",
  "rails": "rails",
  "payment rails": "rails",
  "compliance": "compliance",
  "KYC": "compliance",
  "AML": "compliance",
  "AML/KYC": "compliance",
  "KYC/AML": "compliance",
  "conversion": "autoConvert",
  "convert": "autoConvert",
  "converting": "autoConvert",
  "liquidation": "liquidationAddress",
  "liquidation address": "liquidationAddress",
  "virtual account": "virtualAccount",
  "payout route": "payoutRoute",
  "payout routes": "payoutRoute",
  "multi-currency wallet": "wallet",
  "wallet": "wallet",
  "ACH": "ach",
  "SEPA": "sepa",
  "SWIFT": "swift",
  "SPEI": "spei",
  "PIX": "pix",
}

/**
 * Formats text with SmartTerm tooltips for technical terms
 */
export function formatWithTooltips(text: string): React.ReactNode {
  // Split text by technical terms (case-insensitive)
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let currentIndex = 0

  // Create regex pattern from all terms (longest first to avoid partial matches)
  const sortedTerms = Object.keys(TERM_MAP).sort((a, b) => b.length - a.length)
  const pattern = new RegExp(`\\b(${sortedTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'gi')

  let match
  while ((match = pattern.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }

    // Add SmartTerm for matched term
    const matchedTerm = match[0].toLowerCase()
    const termKey = TERM_MAP[matchedTerm] || TERM_MAP[matchedTerm.toLowerCase()]
    if (termKey) {
      parts.push(
        <SmartTerm key={`term-${currentIndex++}`} termKey={termKey}>
          {match[0]}
        </SmartTerm>
      )
    } else {
      parts.push(match[0])
    }

    lastIndex = pattern.lastIndex
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  // If no matches, return original text
  if (parts.length === 0) {
    return text
  }

  return <>{parts}</>
}

